import type { Problem } from '@sportik/api-coach/problem';
import type { components } from '@sportik/api-coach/schema';
import {
  type InfiniteData,
  infiniteQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { addMonths, parseISO } from 'date-fns';
import { browserTimeZone, localDate, today } from '@/lib/dates';
import { api } from '@/services/api-client';

type ClientWorkoutList = components['schemas']['ClientWorkoutList'];
type Cursor = string | undefined;

const SESSIONS = '/clients/{clientId}/workouts';

/** The one definition of this read: the client's newest session, alone. */
export function lastSessionOptions(clientId: string) {
  return api.queryOptions('get', SESSIONS, {
    params: { path: { clientId }, query: { limit: 1 } },
  });
}

// The list is newest first. `null` says the read is whole and holds nothing.
const toLastSession = (list: ClientWorkoutList) => list.items[0] ?? null;

/** The client's last session, or `null` when none is recorded or shared. */
export function useLastSession(clientId: string) {
  return useQuery({ ...lastSessionOptions(clientId), select: toLastSession });
}

const toSharedFrom = (list: ClientWorkoutList) => list.availableFrom ?? null;

/**
 * The instant the athlete's training records are shared from, or `null` when
 * nothing is held back.
 */
export function useSharedFrom(clientId: string) {
  return useQuery({ ...lastSessionOptions(clientId), select: toSharedFrom });
}

/**
 * The one definition of this read: the client's sessions, newest first, ten
 * at a time. The typed client has no options for a paged read, so the pages
 * are fetched here under the key it generates.
 */
export function sessionsOptions(clientId: string) {
  const params = { path: { clientId }, query: { limit: 10 } };
  const { queryKey } = api.queryOptions('get', SESSIONS, { params });
  // Every failure of the client is a `Problem`; the type says so here, as the
  // typed client's own reads do.
  return infiniteQueryOptions<
    ClientWorkoutList,
    Problem,
    InfiniteData<ClientWorkoutList, Cursor>,
    typeof queryKey,
    Cursor
  >({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const { data, error } = await api.client.GET(SESSIONS, {
        params: { ...params, query: { ...params.query, cursor: pageParam } },
        signal,
      });
      if (error) throw error;
      return data;
    },
    initialPageParam: undefined,
    getNextPageParam: (page) => page.nextCursor,
  });
}

/** The client's sessions, newest first, with older pages on demand. */
export function useSessions(clientId: string) {
  return useInfiniteQuery(sessionsOptions(clientId));
}

/** One session whole: each exercise with its planned and performed sets. */
export function useWorkout(clientId: string, workoutId: string) {
  return api.useQuery('get', '/clients/{clientId}/workouts/{workoutId}', {
    params: { path: { clientId, workoutId } },
  });
}

/**
 * The one definition of this read: each day of one month, `yyyy-MM`, with its
 * sessions and whether the plan puts one there.
 */
export function trainingDaysOptions(
  clientId: string,
  month = today().slice(0, 7),
) {
  const first = parseISO(`${month}-01`);
  return api.queryOptions('get', '/clients/{clientId}/training-days', {
    params: {
      path: { clientId },
      query: {
        fromDate: localDate(first),
        toDate: localDate(addMonths(first, 1)),
        timeZone: browserTimeZone(),
      },
    },
  });
}

/** The client's training days in one month. */
export function useTrainingDays(clientId: string, month: string) {
  return useQuery(trainingDaysOptions(clientId, month));
}
